import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { UniqueIdentifier } from '@dnd-kit/core'
import { Card, CardBody } from '@evertel/web/ui'

import { DnDRow } from './DnDRow'
import { DragData, ParsedRow, ParsedItem } from './types'

interface DnDBoardProps {
    rows: ParsedRow[]
    activeId: UniqueIdentifier | null
    activeDragData: DragData | null
    overId: UniqueIdentifier | null
    schemaId: number
    onDeleteRow: (rowIndex: number) => void
    onDeleteElement: (rowIndex: number, fieldId: string) => void
    onEditElement: (rowIndex: number, item: ParsedItem) => void
    onClickToEditListSchema: (internalSchemaId: string) => void
}

/**
 * Main form builder board component.
 * Contains all rows in a SortableContext for drag-and-drop reordering.
 * Also acts as a droppable area for new layout items from the toolbox.
 */
export const DnDBoard: React.FC<DnDBoardProps> = ({
    rows,
    activeId,
    activeDragData,
    overId,
    schemaId,
    onDeleteRow,
    onDeleteElement,
    onEditElement,
    onClickToEditListSchema
}) => {
    const hasRows = rows.length > 0

    // Check if we're dragging a layout item from toolbox vs reordering existing
    const isDraggingLayout = activeDragData?.type === 'layout'
    const isDraggingElement = activeDragData?.type === 'element'
    const isDraggingFromToolbox = isDraggingLayout && activeDragData?.fromToolbox
    const isReorderingExistingRow = isDraggingLayout && !activeDragData?.fromToolbox

    // Single droppable for layout items from toolbox
    // When no rows: attached to canvas (entire area is droppable)
    // When has rows: attached to end zone (only bottom area is droppable)
    // Disabled when:
    // - Reordering existing rows (to prevent gaps from capturing the drag)
    // - Dragging elements (they should only drop into rows, not the board)
    const { setNodeRef, isOver } = useDroppable({
        id: 'form-builder-droppable',
        data: {
            type: 'form-builder',
            accepts: ['layout']
        },
        disabled: isReorderingExistingRow || isDraggingElement
    })

    // Generate row IDs for SortableContext
    const rowIds = rows.map((_, index) => `row-${index}`)

    // Determine canvas classes
    const canvasClasses = ['sb-board__canvas']
    if (!hasRows && isDraggingFromToolbox) {
        canvasClasses.push('sb-board__canvas--droppable')
        if (isOver) {
            canvasClasses.push('sb-board__canvas--active')
        }
    }

    return (
        <Card className="sb-board">
            <CardBody
                ref={hasRows ? undefined : setNodeRef}
                className={canvasClasses.join(' ')}
            >
                <SortableContext
                    items={rowIds}
                    strategy={verticalListSortingStrategy}
                >
                    {rows.length === 0 ? (
                        <div className="sb-board__empty-state text-center text-muted py-5">
                            <p>Drag layout items here to build your form</p>
                        </div>
                    ) : (
                        rows.map((row) => {
                            // Check if this row is the drop target for layout items from toolbox
                            const isRowDropTarget = activeDragData?.type === 'layout' &&
                                activeDragData?.fromToolbox &&
                                overId === `row-${row.index}`

                            return (
                                <DnDRow
                                    key={`row-${row.index}`}
                                    row={row}
                                    activeId={activeId}
                                    activeDragData={activeDragData}
                                    isDropTarget={isRowDropTarget}
                                    schemaId={schemaId}
                                    onDelete={() => onDeleteRow(row.index)}
                                    onDeleteElement={(fieldId) => onDeleteElement(row.index, fieldId)}
                                    onEditElement={(item) => onEditElement(row.index, item)}
                                    onClickToEditListSchema={onClickToEditListSchema}
                                />
                            )
                        })
                    )}
                </SortableContext>

                {/* Drop zone at end - only when there ARE rows */}
                {hasRows && isDraggingLayout && activeDragData?.fromToolbox && (
                    <div
                        ref={setNodeRef}
                        className={`sb-board__drop-zone ${isOver ? 'sb-board__drop-zone--active' : ''}`}
                    >
                        Drop here to add at the end
                    </div>
                )}
            </CardBody>
        </Card>
    )
}
